import {Fragment, useContext} from 'react';

import {Grid} from '@mui/material';

import {CultureAnalysisPdfContext} from '../../../../contexts/CultureAnalysisPdfContext/CultureAnalysisPdfContext';
import {BenefitClass} from '../../../../features/company/culture/dashboard/types/CultureDashboardBenefitsTypes';
import {AnalysisGuide} from '../../../../features/company/pdf/AnalysisGuide/AnalysisGuide';
import {AppendixPages} from '../../../../features/company/pdf/AppendixPages/AppendixPages';
import {CoverPage} from '../../../../features/company/pdf/CoverPage';
import {CultureHouseFirstPage} from '../../../../features/company/pdf/CultureHouse/CultureHouseFirstPage';
import {CultureHouseSecondPage} from '../../../../features/company/pdf/CultureHouse/CultureHouseSecondPage';
import {DreamEmployer} from '../../../../features/company/pdf/DreamEmployer';
import {DreamEmployerComparisonFirstPage} from '../../../../features/company/pdf/DreamEmployerComparisonFirstPage';
import {DreamEmployerComparisonSecondPage} from '../../../../features/company/pdf/DreamEmployerComparisonSecondPage';
import {EmpionRecommendationsFirstPage} from '../../../../features/company/pdf/EmpionRecommendationsFirstPage';
import {EmpionRecommendationsSecondPage} from '../../../../features/company/pdf/EmpionRecommendationsSecondPage';
import {EmployeeDesiredBenefits} from '../../../../features/company/pdf/EmployeeDesiredBenefits';
import {EmployeeFeedback} from '../../../../features/company/pdf/EmployeeFeedback';
import {EmployeeRecommendations} from '../../../../features/company/pdf/EmployeeRecommendations';
import {ExecutiveSummary} from '../../../../features/company/pdf/ExecutiveSummary';
import {Introduction} from '../../../../features/company/pdf/Introduction';
import {KeyData} from '../../../../features/company/pdf/KeyData';
import {PdfPageSection} from '../../../../features/company/pdf/PdfPageSection';
import {PillarComparisons} from '../../../../features/company/pdf/PillarComparisons';
import {SatisfactionAnalysisFirstPage} from '../../../../features/company/pdf/SatisfactionAnalysis/SatisfactionAnalysisFirstPage';
import {SatisfactionAnalysisSecondPage} from '../../../../features/company/pdf/SatisfactionAnalysis/SatisfactionAnalysisSecondPage';
import {SatisfactionAnalysisThirdPage} from '../../../../features/company/pdf/SatisfactionAnalysis/SatisfactionAnalysisThirdPage';
import {SectionCoverFour} from '../../../../features/company/pdf/section-covers/SectionCoverFour';
import {SectionCoverThree} from '../../../../features/company/pdf/section-covers/SectionCoverThree';
import {SectionCoverTwo} from '../../../../features/company/pdf/section-covers/SectionCoverTwo';
import {Strengths} from '../../../../features/company/pdf/Strengths';
import {pdfPrintPage} from '../../../../features/company/pdf/styles';
import {TableOfContents} from '../../../../features/company/pdf/TableOfContents';
import {ThankYouPage} from '../../../../features/company/pdf/ThankYouPage';

export const CultureAnalysisPdfComponentWrapper = () => {
  const {data, loading} = useContext(CultureAnalysisPdfContext);

  if (loading || !data) {
    return <></>;
  }

  const empionRecommendations = data.empionRecommendations;

  const desiredBenefitsList = data.employeeDesiredBenefits.all.sort(
    (a, b) => (b.percentage || 0) - (a.percentage || 0)
  );

  const renderEmployeeDesiredBenefits = (list: BenefitClass[]) => {
    const pages = [];
    const totalPages = Math.ceil(list.length / 13);
    for (let i = 0; i < list.length; i += 13) {
      const items = list.slice(i, i + 13);
      const pageCount = `${i / 13 + 1}/${totalPages}`;
      pages.push(() => <EmployeeDesiredBenefits items={items} pageCount={pageCount} />);
    }
    return pages;
  };

  const components = [
    CoverPage,
    KeyData,
    Introduction,
    TableOfContents,
    AnalysisGuide,
    ExecutiveSummary,
    SectionCoverTwo,
    CultureHouseFirstPage,
    CultureHouseSecondPage,
    PillarComparisons,
    SectionCoverThree,
    DreamEmployer,
    DreamEmployerComparisonFirstPage,
    DreamEmployerComparisonSecondPage,
    SectionCoverFour,
    EmployeeFeedback,
    Strengths,
    SatisfactionAnalysisFirstPage,
    SatisfactionAnalysisSecondPage,
    SatisfactionAnalysisThirdPage,
    EmployeeRecommendations,
    ...renderEmployeeDesiredBenefits(desiredBenefitsList),
    EmpionRecommendationsFirstPage,
    EmpionRecommendationsSecondPage,
    ThankYouPage,
    AppendixPages,
  ];

  return (
    <PdfPageSection bgColor={'transparent'}>
      {components.map((Component, index) => {
        // Hide EmpionRecommendationsFirstPage and EmpionRecommendationsSecondPage if there are no recommendations,
        // since they don't exist for older cultural surveys
        if (
          (Component === EmpionRecommendationsFirstPage || Component === EmpionRecommendationsSecondPage) &&
          (!empionRecommendations || empionRecommendations.length === 0)
        ) {
          return <Fragment key={index} />;
        }

        const isLastComponent = index === components.length - 1;

        return (
          <Grid
            key={index}
            sx={{
              ...pdfPrintPage,
              ...(isLastComponent ? {} : {breakAfter: 'page'}),
            }}
          >
            <Component />
          </Grid>
        );
      })}
    </PdfPageSection>
  );
};
